import {
  Chip,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { DateTime } from 'luxon';
import { useMemo, useState } from 'react';

import { formatDurationMs } from './format.js';
import PatientFilter from './PatientFilter.js';
import SummaryStat from './SummaryStat.js';
import { useMediaDowntime } from '../../api/media-status.api.js';
import { type Patient } from '../../models/index.js';
import Table from '../Table/Table.js';

function formatTimestamp(iso: string): string {
  return DateTime.fromISO(iso).toLocaleString(DateTime.DATETIME_MED);
}

const COLUMNS: GridColDef[] = [
  { field: 'patient', headerName: 'Patient', flex: 1 },
  { field: 'camera', headerName: 'Camera', flex: 1 },
  { field: 'quality', headerName: 'Quality', width: 90 },
  { field: 'slot', headerName: 'Server', width: 110 },
  {
    field: 'startedAt',
    headerName: 'Dropped at',
    width: 170,
    renderCell: (params) => formatTimestamp(params.value as string),
  },
  {
    field: 'endedAt',
    headerName: 'Recovered at',
    width: 170,
    renderCell: (params) =>
      params.value
        ? formatTimestamp(params.value as string)
        : <Chip size="small" label="Ongoing" color="warning" />,
  },
  {
    field: 'durationMs',
    headerName: 'Duration',
    type: 'number',
    width: 110,
    renderCell: (params) => formatDurationMs(params.value as number),
  },
  {
    field: 'duringMonitoring',
    headerName: 'Monitoring hours',
    width: 140,
    renderCell: (params) =>
      params.value
        ? <Chip size="small" label={formatDurationMs(params.row.monitoringMs as number)} color="error" />
        : <Chip size="small" label="Outside" variant="outlined" />,
  },
  { field: 'source', headerName: 'Source', width: 100 },
];

const DowntimeTab = () => {
  const [month, setMonth] = useState<DateTime>(DateTime.utc().startOf('month'));
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [monitoringOnly, setMonitoringOnly] = useState(false);

  const downtime = useMediaDowntime({
    month: month.toFormat('yyyy-MM'),
    patientId: selectedPatient?.id,
  });

  const events = useMemo(() => {
    const all = downtime.data?.events ?? [];
    return monitoringOnly ? all.filter((event) => event.duringMonitoring) : all;
  }, [downtime.data, monitoringOnly]);

  const summary = downtime.data?.summary ?? {
    drops: 0,
    dropsDuringMonitoring: 0,
    downtimeMs: 0,
    monitoringDowntimeMs: 0,
  };

  const rows = useMemo(() => events.map((event) => ({
    id: event.id,
    patient: event.patientName ?? '—',
    camera: event.cameraName ?? event.cameraId,
    quality: event.quality,
    slot: event.slot ?? '—',
    startedAt: event.startedAt,
    endedAt: event.endedAt,
    durationMs: event.durationMs,
    duringMonitoring: event.duringMonitoring,
    monitoringMs: event.monitoringMs,
    source: event.source,
  })), [events]);

  return (
    <>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Connection drops per stream: when the camera feed dropped, when it recovered, and how much
        of the outage fell inside the patient&apos;s monitoring hours. Downtime totals count
        wall-clock time with at least one stream down — simultaneous outages are not double-counted.
      </Typography>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          alignItems={{ md: 'center' }}
          justifyContent="space-between"
        >
          <Stack direction="row" spacing={4} flexWrap="wrap" useFlexGap>
            <SummaryStat label="Drops" value={summary.drops.toString()} emphasised />
            <SummaryStat label="During monitoring" value={summary.dropsDuringMonitoring.toString()} />
            <SummaryStat label="Downtime" value={formatDurationMs(summary.downtimeMs)} />
            <SummaryStat label="Monitoring downtime" value={formatDurationMs(summary.monitoringDowntimeMs)} />
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
            <FormControlLabel
              control={
                <Switch
                  checked={monitoringOnly}
                  onChange={(event) => setMonitoringOnly(event.target.checked)}
                />
              }
              label="Monitoring hours only"
            />
            <PatientFilter
              id="media-downtime-patient-filter"
              value={selectedPatient}
              onChange={setSelectedPatient}
            />
            <DatePicker
              label="Month"
              views={['year', 'month']}
              openTo="month"
              value={month}
              onChange={(value) => value && setMonth(value.startOf('month'))}
              disableFuture
              slotProps={{ textField: { size: 'small' } }}
            />
          </Stack>
        </Stack>
      </Paper>

      <Table
        columns={COLUMNS}
        rows={rows}
        pageSize={25}
        isLoading={downtime.isLoading}
        NoRowsOverlayText="No connection drops recorded in this period."
        toolbar
      />
    </>
  );
};

export default DowntimeTab;
